// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The dot-matrix display, as a lit panel behind glass (M3-17).
//
// A DMD is not a screen showing an image. It is 4,096 individually lit lamps behind a sheet of
// smoked plastic, and the two do not look remotely alike: the dots are round with visible gaps
// between them, an unlit dot is a *dark circle* rather than nothing at all, and a lit one
// blooms into the several dots around it. Drawing the raster as a nearest-filtered texture
// gives you sharp rectangles on a black field — legible, and instantly recognisable as not a
// pinball machine.
//
// So the raster goes to the GPU as data and a shader turns it into dots. The division of
// labour is deliberate:
//
//   * **`dmdPlacement.ts`** decides where the panel goes. Arithmetic, and tested.
//   * **`packDots`** turns 4-bit levels into a texture. Arithmetic, and tested.
//   * **The shader** decides what a dot looks like. Appearance only — there is no number in it
//     that anything else depends on, so there is nothing for a headless test to protect.
//
// The shader is where all the untestable code lives, and it is kept to exactly that.

import {
  DataTexture,
  Mesh,
  NearestFilter,
  PlaneGeometry,
  RedFormat,
  ShaderMaterial,
  UnsignedByteType,
  Vector3,
  type BufferGeometry,
  type Material,
} from 'three'
import { DMD_LEVELS, dmdPalette, type DmdRaster } from '../fx/dmd'
import { placePanel, type ArtWindow } from './dmdPlacement'

export interface BuiltDmdPanel {
  readonly mesh: Mesh
  readonly geometries: readonly BufferGeometry[]
  readonly materials: readonly Material[]
  /**
   * Push the current raster to the GPU.
   *
   * Called once a frame. The upload is 4 kB — a 128 × 32 single-channel texture — which is
   * less than one mid-sized icon, so there is nothing to be gained by tracking whether the
   * raster changed and a correctness risk in getting that tracking wrong.
   */
  readonly update: (raster: DmdRaster) => void
  readonly dispose: () => void
}

/**
 * Build the panel for a machine's display, sized and placed from its own backglass art.
 *
 * Returns the mesh in the glass's local frame; the caller parents it to the glass so the
 * backbox's lean carries once rather than twice.
 */
export function buildDmdPanel(
  window: ArtWindow,
  glassWidth: number,
  glassHeight: number,
  raster: DmdRaster,
  palette = 'amber',
): BuiltDmdPanel {
  const place = placePanel(window, glassWidth, glassHeight)

  const geometry = new PlaneGeometry(place.width, place.height)
  const texture = new DataTexture(
    new Uint8Array(raster.width * raster.height),
    raster.width,
    raster.height,
    RedFormat,
    UnsignedByteType,
  )
  // Nearest, and it matters: the shader wants the *cell's own* level, and a linear fetch
  // would blend two neighbouring dots together before the dot mask ever ran, softening every
  // edge in the display and turning small text into a grey smear.
  texture.magFilter = NearestFilter
  texture.minFilter = NearestFilter
  texture.generateMipmaps = false
  // A DataTexture defaults to a bottom-up read; the raster's row 0 is its top row.
  texture.flipY = true
  texture.needsUpdate = true

  const colour = dmdPalette(palette)
  const material = new ShaderMaterial({
    uniforms: {
      uDots: { value: texture },
      uGrid: { value: new Vector3(raster.width, raster.height, 0) },
      uOn: { value: new Vector3(colour[0], colour[1], colour[2]) },
    },
    vertexShader: VERTEX,
    fragmentShader: FRAGMENT,
    transparent: false,
  })

  const mesh = new Mesh(geometry, material)
  mesh.name = 'dmd'
  mesh.position.set(place.x, place.y, DMD_STANDOFF)

  const update = (current: DmdRaster): void => {
    packDots(current, texture.image.data as Uint8Array)
    texture.needsUpdate = true
  }
  update(raster)

  return {
    mesh,
    geometries: [geometry],
    materials: [material],
    update,
    dispose: () => texture.dispose(),
  }
}

/**
 * 4-bit levels to 8-bit texels, in place.
 *
 * `level × 17` rather than `level × 16`: fifteen has to reach 255, not 240. Scaling by 16
 * costs the brightest dot six per cent of its brightness, which nobody would ever notice and
 * which would make the display permanently, unaccountably dim.
 */
export function packDots(raster: DmdRaster, into: Uint8Array): Uint8Array {
  const n = Math.min(raster.dots.length, into.length)
  for (let i = 0; i < n; i++) into[i] = raster.dots[i]! * (255 / (DMD_LEVELS - 1))
  return into
}

/** Millimetres the panel stands proud of the glass, so it does not z-fight with the art. */
const DMD_STANDOFF = 0.0028

const VERTEX = `
varying vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`

// Three things a real DMD does that a texture does not, and each of them is one line here:
//
//   * The dots are **round, with gaps**. A lit dot does not fill its cell.
//   * An **unlit dot is still there** — a dark grey circle on a black panel. Leave it out and
//     the display looks like a hole in the backglass whenever it is mostly blank.
//   * A lit dot **blooms** into its neighbours through the smoked plastic. The 3 × 3 gather is
//     nine taps of a 128 × 32 texture, which is free, and it is what makes the display look
//     like it is glowing rather than like it is printed.
const FRAGMENT = `
precision highp float;
uniform sampler2D uDots;
uniform vec3 uGrid;
uniform vec3 uOn;
varying vec2 vUv;

float levelAt(vec2 cell) {
  vec2 uv = (cell + 0.5) / uGrid.xy;
  if (uv.x < 0.0 || uv.x > 1.0 || uv.y < 0.0 || uv.y > 1.0) return 0.0;
  return texture2D(uDots, uv).r;
}

void main() {
  vec2 grid = uGrid.xy;
  vec2 cell = vUv * grid;
  vec2 index = floor(cell);
  vec2 offset = fract(cell) - 0.5;

  // The dot itself: a disc of 0.38 of a cell, soft-edged by roughly a pixel's worth of the
  // cell so it does not alias into a stair-step when the panel is small on screen.
  float radius = length(offset);
  float edge = fwidth(radius) + 0.02;
  float mask = 1.0 - smoothstep(0.34 - edge, 0.34 + edge, radius);

  float level = levelAt(index);

  // Bloom, gathered from the neighbourhood. Weighted by distance from *this fragment* rather
  // than from the cell centre, so the glow between two lit dots joins up.
  float glow = 0.0;
  for (int dy = -1; dy <= 1; dy++) {
    for (int dx = -1; dx <= 1; dx++) {
      vec2 d = vec2(float(dx), float(dy));
      float l = levelAt(index + d);
      float r = length(offset - d);
      glow += l * exp(-r * r * 1.35);
    }
  }

  // A phosphor is brightest at the centre of the dot and falls away across it.
  float core = level * mask * (1.0 - 0.45 * radius / 0.34);

  vec3 unlit = vec3(0.035, 0.032, 0.030) * mask;
  vec3 lit = uOn * (core * 1.15 + glow * 0.09);
  // The panel between the dots is not black: smoked plastic picks up the glow behind it.
  vec3 plastic = uOn * glow * 0.02;

  gl_FragColor = vec4(unlit + lit + plastic, 1.0);
}
`
